import type { LucideIcon } from 'lucide-react';
import { LogOut, Menu, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

export interface SidebarNavItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

interface SidebarProps {
  navItems: SidebarNavItem[];
  logoText: string;
  logoAccent: string;
  userName: string;
  userRole: string;
  onLogout: () => void;
}

const STORAGE_KEY = 'sidebar:expanded';

export function Sidebar({
  navItems,
  logoText,
  logoAccent,
  userName,
  userRole,
  onLogout,
}: SidebarProps) {
  // Đọc ngay lúc khởi tạo state, không qua useEffect — nếu không sidebar sẽ
  // hiện mở rồi thu lại ngay ở lần vẽ đầu.
  const [expanded, setExpanded] = useState(() => localStorage.getItem(STORAGE_KEY) !== 'false');

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(expanded));
  }, [expanded]);

  return (
    <aside
      className={`border-line bg-panel flex h-screen flex-col border-r transition-[width] duration-200 ease-in-out ${
        expanded ? 'w-[220px]' : 'w-[60px]'
      }`}
    >
      {/* ── Header: toggle + logo ── */}
      <div className="border-line flex h-14 shrink-0 items-center gap-2.5 border-b px-3.5">
        <button
          type="button"
          onClick={() => {
            setExpanded((v) => !v);
          }}
          aria-label={expanded ? 'Thu gọn thanh bên' : 'Mở rộng thanh bên'}
          className="text-ink-dim hover:bg-panel-hover hover:text-ink shrink-0 rounded-md p-1 transition-colors"
        >
          <Menu className="h-[18px] w-[18px]" />
        </button>
        {expanded && (
          <span className="text-ink whitespace-nowrap text-[15px] font-semibold">
            {logoText} <strong className="text-accent">{logoAccent}</strong>
          </span>
        )}
      </div>

      {/* ── Nav ── */}
      <nav className="flex-1 overflow-y-auto py-2">
        {navItems.map(({ path, label, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            title={expanded ? undefined : label}
            className={({ isActive }) =>
              `mx-2 my-0.5 flex items-center gap-2.5 whitespace-nowrap rounded-lg px-3.5 py-2.5 text-[13px] font-medium transition-all duration-150 ${
                expanded ? '' : 'justify-center px-2.5'
              } ${
                isActive
                  ? 'bg-accent-soft text-accent'
                  : 'text-ink-dim hover:bg-panel-hover hover:text-ink'
              }`
            }
          >
            <Icon className="h-[18px] w-[18px] shrink-0" />
            {expanded && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* ── Footer: user + đăng xuất ── */}
      <div className="border-line mt-auto flex flex-col gap-1 border-t p-2">
        <div
          className={`flex min-h-[46px] items-center gap-2.5 rounded-lg p-2 ${
            expanded ? '' : 'justify-center'
          }`}
        >
          <div className="border-accent bg-accent-soft text-accent flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border">
            <User className="h-4 w-4" />
          </div>
          {expanded && (
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="text-ink truncate text-[13px] font-semibold">{userName}</span>
              <span className="text-ink-faint text-[10px] font-medium uppercase tracking-[0.08em]">
                {userRole}
              </span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onLogout}
          title={expanded ? undefined : 'Đăng xuất'}
          className={`text-ink-dim hover:bg-danger-soft hover:text-danger flex min-h-[36px] w-full items-center gap-2 rounded-lg px-2 py-2 text-[13px] transition-colors ${
            expanded ? '' : 'justify-center'
          }`}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {expanded && <span>Đăng xuất</span>}
        </button>
      </div>
    </aside>
  );
}
